import React, { useState } from "react";
import { FaArrowRight, FaRegCirclePlay, FaRegPenToSquare, FaSitemap, FaShieldHalved, FaHeadset } from "react-icons/fa6";
import { Container } from "../home/ui";

// Hero artwork (laptop + phone mockup). On desktop (xl+) it is a background on the right side
// of the section; on mobile/tablet it shows below the text.
// Put the file at public/img/custom-website-hero.jpg (or change the path). Until it exists,
// the website-development hero art is shown instead.

const heroImg = "/img/custom-website-hero.jpg";
const fallbackImg = "/img/website-dev-hero.jpg";

const features = [
  { icon: FaRegPenToSquare, title: "100% Custom Design", text: "Built around your brand and goals" },
  { icon: FaSitemap, title: "Scalable & Flexible", text: "Grow without limitations" },
  { icon: FaShieldHalved, title: "Secure & Reliable", text: "Best security practices and performance" },
  { icon: FaHeadset, title: "Ongoing Support", text: "We're here even after launch" },
];

// Fades all four edges of the image so its own background never shows as a box.
const fadeX = "linear-gradient(to right, transparent 0%, #000 10%, #000 92%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 8%, #000 90%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const CustomHero = () => {
  const [src, setSrc] = useState(heroImg);
  const onError = () => src !== fallbackImg && setSrc(fallbackImg);

  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#FCFCFE] via-[#F8FAFF] to-[#F2F5FE] pb-[48px] pt-[110px] xl:pb-[44px] xl:pt-[calc(clamp(76px,5.83vw,112px)+28px)]">
      {/* Desktop: artwork as background on the right (natural ratio, never stretched or cropped) */}
      <img
        src={src}
        onError={onError}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-[1vw] top-[calc(clamp(76px,5.83vw,112px)+8px)] hidden h-[min(30vw,580px)] w-auto max-w-none select-none xl:block"
        style={edgeFade}
      />

      <Container className="relative grid max-w-[1900px] items-center gap-[32px] xl:grid-cols-[minmax(0,44%)_1fr] xl:gap-[24px] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div>
          <span className="inline-flex items-center gap-[8px] rounded-full bg-[#E9F0FF] px-[16px] py-[7px] text-[13px] font-medium text-[#1E4DFF]">
            <span className="h-[6px] w-[6px] rounded-full bg-[#1E4DFF]" />
            Custom Website Development
          </span>

          <h1 className="mt-[20px] text-[38px] font-bold leading-[1.12] tracking-tight text-[#0B1B4D] sm:text-[52px] xl:text-[clamp(42px,3.4vw,64px)]">
            Tailor-Made Websites
            <br />
            for Your Unique
            <br />
            <span className="text-gradient">Business Needs</span>
          </h1>

          <p className="mt-[18px] max-w-[580px] text-[16px] leading-[1.6] text-slate-600 sm:text-[17px] xl:text-[clamp(16px,1.05vw,19px)]">
            We build custom websites that are fast, secure, scalable and designed to grow with your business. Get a
            website that reflects your brand, engages your audience and drives real results.
          </p>

          <div className="mt-[28px] flex flex-wrap items-center gap-[16px]">
            <a
              href="/contact-us"
              className="group inline-flex h-[54px] items-center gap-[10px] rounded-full bg-gradient-to-r from-[#0E7BFF] via-[#3D5BF6] to-[#A21CF0] px-[30px] text-[16px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(91,61,245,0.7)] transition hover:brightness-110"
            >
              Get a Free Consultation
              <FaArrowRight className="text-[14px] transition group-hover:translate-x-1" />
            </a>
            <a
              href="/our-completed-projects"
              className="inline-flex h-[54px] items-center gap-[10px] rounded-full border-[1.5px] border-solid border-[#9DB6FF] bg-white/70 px-[28px] text-[16px] font-medium text-[#1E3FB8] transition hover:bg-[#EEF3FF]"
            >
              <FaRegCirclePlay className="text-[20px]" />
              View Our Work
            </a>
          </div>
        </div>

        {/* Mobile / tablet: artwork below the text */}
        <div className="flex justify-center xl:hidden">
          <img
            src={src}
            onError={onError}
            alt="Custom website shown on a laptop and a phone"
            className="h-auto w-full max-w-[760px] select-none object-contain"
            style={edgeFade}
          />
        </div>
      </Container>

      {/* Feature strip */}
      <Container className="relative mt-[24px] max-w-[1900px] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div className="grid max-w-[1020px] grid-cols-1 gap-[22px] sm:grid-cols-2 lg:grid-cols-4 lg:gap-0 xl:max-w-[min(1020px,72%)]">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`flex items-start gap-[16px] lg:py-[6px] lg:pr-[18px] ${
                i === 0 ? "" : "lg:border-y-0 lg:border-l-[1px] lg:border-r-0 lg:border-solid lg:border-[#E6EAF3] lg:pl-[24px]"
              }`}
            >
              <span className="inline-flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#EEF2FF] to-[#F3EEFF] text-[19px] text-[#2F54EB] shadow-[0_6px_16px_-8px_rgba(47,84,235,0.45)]">
                <f.icon />
              </span>
              <div className="min-w-0">
                <p className="whitespace-nowrap text-[14px] font-semibold leading-[1.3] text-[#0B1B4D]">{f.title}</p>
                <p className="mt-[6px] max-w-[175px] text-[13px] leading-[1.55] text-slate-500">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default CustomHero;
